import { useCallback, useEffect, useRef, useState } from 'react';
import type React from 'react';
import { clsx } from 'clsx';

const gooDropletAsset = new URL('./assets/goo-droplet.svg', import.meta.url).href;
const cursorSpriteAsset = new URL('./assets/cursorSprite.webp', import.meta.url).href;

type Point = {
  x: number;
  y: number;
};

type GooLink = Point & {
  vx: number;
  vy: number;
  mass: number;
  stuck: boolean;
  freeTail: boolean;
  committed: boolean;
  anchorAge: number;
  anchorDelay: number;
};

type GooStrand = {
  id: number;
  links: GooLink[];
  age: number;
  drawing: boolean;
  source: Point;
  collisionDelay: number;
  emitTick: number;
  spurtAge: number;
};

type GooDroplet = Point & {
  id: number;
  vx: number;
  vy: number;
  alpha: number;
  age: number;
  baseSize: number;
  sizeFactor: number;
  sizeDecrease: number;
  rotation: number;
};

type ImageMetrics = {
  x: number;
  y: number;
  width: number;
  height: number;
};

type GooMiniGameProps = {
  imageSrc: string;
  label: string;
  onBackToResults: () => void;
  backButtonClassName?: string;
  onBackPointerDownCapture?: () => void;
  onBackTouchStartCapture?: () => void;
  onBackMouseDownCapture?: () => void;
};

type GooDebugMetrics = {
  frame: number;
  activeStrandId: number | null;
  activePointerId: number | null;
  pointerActive: boolean;
  strands: number;
  drawingStrands: number;
  stuckLinks: number;
  lockWrites: number;
  lastFinalizeReason: string;
};

type PersistedStrandMark = {
  links: GooLink[];
  alpha: number;
  thickness: number;
};

type CursorSpriteState = {
  active: boolean;
  followsPointer: boolean;
  position: Point | null;
  startedAt: number;
};

const GRAVITY = 1.35;
const FRICTION = 1.18;
const MOVE_SMOOTHING = 8;
const MAX_STRAND_LINKS = 18;
const LINK_SPACING = 13;
const LOCK_AFTER_FRAMES = 220;
const WET_LOCK_AFTER_FRAMES = 64;
const MASS_FLOW = 0.005;
const MIN_LINK_MASS = 0.01;
const MAX_LINK_MASS = 1.1;
const TAIL_NO_CLUMP_LINKS = 4;
const TAIL_MAX_MASS = 0.42;
const LINK_FREQ = 2;
const MAX_SPURT_FRAMES = 90;
const FORCE_LOCK_AFTER_FRAMES = 300;
const LANDING_SPEED_THRESHOLD = 2.6;
const HORIZONTAL_STICK_VERTICAL_SPEED = 4.2;
const HORIZONTAL_STICK_MIN_SPEED = 4.2;
const HORIZONTAL_STICK_DOMINANCE = 1.35;
const FREE_TAIL_SPRING_DAMPING = 0.45;
const FREE_TAIL_VELOCITY_DAMPING = 1.38;
const FREE_TAIL_GRAVITY_SCALE = 0.72;
const FREE_TAIL_CONSTRAINT_SCALE = 0.42;
const DRAWING_FREE_TAIL_LINKS = 2;
const MIN_LINK_AIRTIME_DELAY = 10;
const UPWARD_AIRTIME_BONUS = 14;
const MIN_PERSIST_DROPLET_SIZE = 5;
const END_START_MIN_DIST_FACTOR = 3.2;
const END_START_PROTECT_FRAMES = 150;
const BASE_UPWARD_LAUNCH = 16.5;
const UPWARD_SPEED_CAP = 22.5;
const SPEED_SAMPLE_THRESHOLD_SQ = 100;
const SPEED_CLAMP_INPUT = 50;
const SPEED_CLAMP_OUTPUT = 30;
const GOO_SHADOW = 'rgba(255, 244, 218, 0.5)';
const GOO_EDGE_COLOR = 'rgba(250, 230, 190, 0.68)';
const GOO_CORE_COLOR = 'rgba(255, 245, 214, 0.9)';
const GOO_SETTLED_TINT = 'rgba(246, 226, 184, 0.86)';
const CURSOR_SPRITE_FRAME_COUNT = 15;
const CURSOR_SPRITE_FRAME_RATE = 24;
const CURSOR_SPRITE_SOURCE_FRAME_WIDTH = 594;
const CURSOR_SPRITE_SOURCE_FRAME_HEIGHT = 512;
const CURSOR_SPRITE_DRAW_WIDTH = 180;
const CURSOR_SPRITE_OFFSET_X = -23;
const CURSOR_SPRITE_OFFSET_Y = -30;
const SHARE_MAX_DIMENSION = 1536;
const SHARE_WATERMARK_TEXT = 'fmk-game.com';
const DEBUG_GOO_OVERLAY = false;

const clamp = (value: number, min: number, max: number): number => Math.min(max, Math.max(min, value));
const distance = (a: Point, b: Point): number => Math.hypot(a.x - b.x, a.y - b.y);

const getPointerPoint = (event: React.PointerEvent<HTMLCanvasElement>, canvas: HTMLCanvasElement): Point => {
  const rect = canvas.getBoundingClientRect();
  return {
    x: ((event.clientX - rect.left) / rect.width) * canvas.width,
    y: ((event.clientY - rect.top) / rect.height) * canvas.height,
  };
};

const isPointInCanvasBounds = (point: Point, canvas: HTMLCanvasElement): boolean =>
  point.x >= 0 && point.x <= canvas.width && point.y >= 0 && point.y <= canvas.height;

const drawContainedImage = (
  ctx: CanvasRenderingContext2D,
  image: HTMLImageElement,
  canvasWidth: number,
  canvasHeight: number
): ImageMetrics => {
  const scale = Math.min(canvasWidth / image.width, canvasHeight / image.height);
  const width = image.width * scale;
  const height = image.height * scale;
  const x = (canvasWidth - width) / 2;
  const y = (canvasHeight - height) / 2;
  ctx.drawImage(image, x, y, width, height);
  return { x, y, width, height };
};

const getContainedImageMetrics = (
  image: HTMLImageElement,
  canvasWidth: number,
  canvasHeight: number
): ImageMetrics => {
  const scale = Math.min(canvasWidth / image.width, canvasHeight / image.height);
  const width = image.width * scale;
  const height = image.height * scale;
  return {
    x: (canvasWidth - width) / 2,
    y: (canvasHeight - height) / 2,
    width,
    height,
  };
};

const isPointInImage = (point: Point, metrics: ImageMetrics): boolean =>
  point.x >= metrics.x &&
  point.x <= metrics.x + metrics.width &&
  point.y >= metrics.y &&
  point.y <= metrics.y + metrics.height;

const waitForAnimationFrame = (): Promise<void> =>
  new Promise((resolve) => {
    window.requestAnimationFrame(() => resolve());
  });

const addCacheBustParam = (src: string): string => {
  const separator = src.includes('?') ? '&' : '?';
  return `${src}${separator}fmkGooShareTs=${Date.now().toString(36)}`;
};

const loadCorsImageFromUrl = async (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Could not load the image for browser download.'));
    image.src = addCacheBustParam(src);
  });

const drawShareWatermark = (ctx: CanvasRenderingContext2D, width: number, height: number): void => {
  const fontSize = Math.max(18, Math.round(width * 0.032));
  const paddingX = Math.max(14, Math.round(fontSize * 0.72));
  const paddingY = Math.max(8, Math.round(fontSize * 0.42));
  const margin = Math.max(12, Math.round(fontSize * 0.55));

  ctx.save();
  ctx.font = `800 ${fontSize}px sans-serif`;
  ctx.textAlign = 'right';
  ctx.textBaseline = 'bottom';
  const textWidth = ctx.measureText(SHARE_WATERMARK_TEXT).width;
  const boxWidth = textWidth + paddingX * 2;
  const boxHeight = fontSize + paddingY * 2;
  const boxX = width - margin - boxWidth;
  const boxY = height - margin - boxHeight;

  ctx.fillStyle = 'rgba(0, 0, 0, 0.62)';
  ctx.fillRect(boxX, boxY, boxWidth, boxHeight);
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.22)';
  ctx.lineWidth = Math.max(1, Math.round(fontSize * 0.06));
  ctx.strokeRect(boxX + 0.5, boxY + 0.5, boxWidth - 1, boxHeight - 1);
  ctx.fillStyle = '#ffffff';
  ctx.shadowColor = 'rgba(0, 0, 0, 0.7)';
  ctx.shadowBlur = 2;
  ctx.fillText(SHARE_WATERMARK_TEXT, width - margin - paddingX, height - margin - paddingY);
  ctx.restore();
};

const createLink = (point: Point, velocity: Point, mass: number, stuck = false, anchorDelay = 0): GooLink => ({
  x: point.x,
  y: point.y,
  vx: velocity.x,
  vy: velocity.y,
  mass,
  stuck,
  freeTail: false,
  committed: false,
  anchorAge: stuck ? 1 : 0,
  anchorDelay,
});

const randomCumMass = (thickness: number): number => (0.3 + Math.random() * 0.4) * thickness;

const getSpeedMultiplier = (thickness: number): number => (thickness - 1) / 2 + 1;

const stickLinkToImage = (link: GooLink, metrics: ImageMetrics): void => {
  const wasStuck = link.stuck;
  link.x = clamp(link.x, metrics.x, metrics.x + metrics.width);
  link.y = clamp(link.y, metrics.y, metrics.y + metrics.height);
  link.vx *= 0.18;
  link.vy *= 0.15;
  link.stuck = true;
  if (!wasStuck) {
    link.anchorAge = 0;
    link.committed = false;
  }
};

const getLinkThickness = (link: GooLink, thickness: number): number => {
  const base = Math.max(1, Math.pow(link.mass * 2, 2)) * thickness;
  return link.stuck ? base * 1.4 : base;
};

const isNewestTailLink = (index: number, linkCount: number): boolean => index >= Math.max(0, linkCount - TAIL_NO_CLUMP_LINKS);

const normalize = (point: Point): Point => {
  const len = Math.hypot(point.x, point.y);
  if (len <= 0.0001) {
    return { x: 0, y: 1 };
  }
  return { x: point.x / len, y: point.y / len };
};

const getTaperedStrandWidth = (avgThickness: number, progress: number, widthScale = 1): number =>
  Math.max(1.25, avgThickness * widthScale * (2.35 - progress * 1.22));

const getWetSegmentAlpha = (bodyAlpha: number, progress: number, settledRatio: number, alphaScale = 1): number =>
  clamp(bodyAlpha * alphaScale * (1 - progress * 0.58 + settledRatio * 0.08), 0.04, 0.98);

const traceGooPathUntil = (
  ctx: CanvasRenderingContext2D,
  links: GooLink[],
  endProgress: number,
  offset: Point = { x: 0, y: 0 }
): void => {
  const head = links[0];
  if (!head) return;
  const finalProgress = clamp(endProgress, 0, 1);
  const finalPosition = finalProgress * (links.length - 1);
  const finalWholeIndex = Math.floor(finalPosition);
  const finalPart = finalPosition - finalWholeIndex;
  const points: Point[] = [{ x: head.x + offset.x, y: head.y + offset.y }];

  for (let i = 1; i <= finalWholeIndex; i += 1) {
    const current = links[i];
    if (!current) continue;
    points.push({ x: current.x + offset.x, y: current.y + offset.y });
  }

  const from = links[finalWholeIndex];
  const to = links[finalWholeIndex + 1];
  if (from && to && finalPart > 0.001) {
    points.push({
      x: from.x + (to.x - from.x) * finalPart + offset.x,
      y: from.y + (to.y - from.y) * finalPart + offset.y,
    });
  }

  const first = points[0];
  if (!first) return;
  ctx.moveTo(first.x, first.y);
  for (let i = 1; i < points.length - 1; i += 1) {
    const current = points[i];
    const next = points[i + 1];
    if (!current || !next) continue;
    const midX = (current.x + next.x) / 2;
    const midY = (current.y + next.y) / 2;
    ctx.quadraticCurveTo(current.x, current.y, midX, midY);
  }
  const last = points[points.length - 1];
  if (last && last !== first) {
    ctx.lineTo(last.x, last.y);
  }
};

const strokeWetLayer = (
  ctx: CanvasRenderingContext2D,
  links: GooLink[],
  avgThickness: number,
  bodyAlpha: number,
  settledRatio: number,
  endProgress: number,
  widthScale: number,
  alphaScale: number,
  offset: Point = { x: 0, y: 0 }
): void => {
  ctx.globalAlpha = getWetSegmentAlpha(bodyAlpha, 0, settledRatio, alphaScale);
  ctx.lineWidth = getTaperedStrandWidth(avgThickness, 0, widthScale);
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.beginPath();
  traceGooPathUntil(ctx, links, endProgress, offset);
  ctx.stroke();
};

const drawGooPath = (ctx: CanvasRenderingContext2D, links: GooLink[], thickness: number, alpha = 1): void => {
  if (links.length < 2) return;

  let totalThickness = 0;
  let totalSpeed = 0;
  let settledCount = 0;
  for (const link of links) {
    totalThickness += getLinkThickness(link, thickness);
    totalSpeed += Math.hypot(link.vx, link.vy);
    if (link.stuck && link.anchorAge > WET_LOCK_AFTER_FRAMES / 3) {
      settledCount += 1;
    }
  }
  const avgThickness = totalThickness / links.length;
  const avgSpeed = totalSpeed / links.length;
  const settledRatio = settledCount / links.length;
  const bodyAlpha =
    alpha *
    clamp(
      0.56 +
        avgThickness / 28 +
        settledRatio * 0.18 -
        Math.min(0.18, avgSpeed * 0.022),
      0.45,
      0.95
    );

  const head = links[0];
  const tail = links[links.length - 1];
  const flow = head && tail ? normalize({ x: tail.x - head.x, y: tail.y - head.y }) : { x: 0, y: 1 };
  const normal = { x: -flow.y, y: flow.x };
  const center = links[Math.floor(links.length / 2)] ?? head ?? tail;
  if (!head || !tail || !center) return;
  const gradRadius = Math.max(24, avgThickness * 2.8);
  const edgeColor = settledRatio > 0.55 ? 'rgba(248, 236, 216, 0.74)' : GOO_EDGE_COLOR;
  const coreColor = settledRatio > 0.55 ? GOO_SETTLED_TINT : GOO_CORE_COLOR;

  ctx.save();
  ctx.shadowColor = GOO_SHADOW;
  ctx.shadowBlur = 13 + settledRatio * 3;
  ctx.strokeStyle = edgeColor;
  strokeWetLayer(ctx, links, avgThickness, bodyAlpha, settledRatio, 1, 0.62, 0.32);
  strokeWetLayer(ctx, links, avgThickness, bodyAlpha, settledRatio, 0.72, 0.92, 0.42);
  strokeWetLayer(ctx, links, avgThickness, bodyAlpha, settledRatio, 0.46, 1.18, 0.34);

  ctx.shadowBlur = 7 + settledRatio * 2;
  ctx.strokeStyle = coreColor;
  strokeWetLayer(ctx, links, avgThickness, bodyAlpha, settledRatio, 1, 0.4, 0.38);
  strokeWetLayer(ctx, links, avgThickness, bodyAlpha, settledRatio, 0.68, 0.62, 0.58);
  strokeWetLayer(ctx, links, avgThickness, bodyAlpha, settledRatio, 0.38, 0.82, 0.48);

  ctx.shadowBlur = 0;
  ctx.strokeStyle = 'rgba(255, 248, 224, 0.46)';
  strokeWetLayer(ctx, links, avgThickness, bodyAlpha, settledRatio, 0.86, 0.14, 0.22, {
    x: normal.x * Math.max(1.2, avgThickness * 0.18),
    y: normal.y * Math.max(1.2, avgThickness * 0.18),
  });

  ctx.globalAlpha = Math.min(1, bodyAlpha + 0.1);
  ctx.fillStyle = 'rgba(255, 247, 219, 0.42)';
  ctx.beginPath();
  ctx.arc(head.x, head.y, Math.max(2.2, getTaperedStrandWidth(avgThickness, 0, 0.24)), 0, Math.PI * 2);
  ctx.fill();

  ctx.globalAlpha = Math.max(0.035, bodyAlpha * 0.12);
  ctx.beginPath();
  ctx.arc(tail.x, tail.y, Math.max(1.4, getTaperedStrandWidth(avgThickness, 1, 0.16)), 0, Math.PI * 2);
  ctx.fill();

  const highlightAlpha = clamp(0.14 + avgThickness / 80 - avgSpeed * 0.01, 0.08, 0.24) * alpha;
  if (highlightAlpha > 0.01) {
    const highlightWidth = Math.max(1.2, avgThickness * 0.22);
    const highlightGradient = ctx.createLinearGradient(
      center.x + normal.x * gradRadius,
      center.y + normal.y * gradRadius,
      center.x - normal.x * gradRadius,
      center.y - normal.y * gradRadius
    );
    highlightGradient.addColorStop(0, 'rgba(255, 248, 224, 0.03)');
    highlightGradient.addColorStop(0.4, 'rgba(255, 249, 229, 0.32)');
    highlightGradient.addColorStop(1, 'rgba(255, 244, 215, 0.04)');
    ctx.globalAlpha = highlightAlpha;
    ctx.strokeStyle = highlightGradient;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    strokeWetLayer(ctx, links, avgThickness, highlightAlpha, settledRatio, 0.82, 0.1, 0.65, {
      x: normal.x * highlightWidth,
      y: normal.y * highlightWidth,
    });
  }

  ctx.restore();
};

const drawLockedDroplet = (ctx: CanvasRenderingContext2D, droplet: GooDroplet): void => {
  const speedMagnitude = Math.hypot(droplet.vx, droplet.vy);
  const stretch = clamp(speedMagnitude / 10, 1, 6);
  const width = droplet.baseSize * 0.28 * droplet.sizeFactor;
  const height = droplet.baseSize * 0.65 * stretch * droplet.sizeFactor;
  const gradient = ctx.createLinearGradient(0, -height, 0, height);
  gradient.addColorStop(0, 'rgba(255, 246, 218, 0.86)');
  gradient.addColorStop(0.45, 'rgba(252, 238, 205, 0.8)');
  gradient.addColorStop(1, 'rgba(246, 232, 208, 0.65)');
  ctx.save();
  ctx.translate(droplet.x, droplet.y);
  ctx.rotate(droplet.rotation);
  ctx.globalAlpha = Math.max(0.35, droplet.alpha);
  ctx.fillStyle = gradient;
  ctx.shadowColor = GOO_SHADOW;
  ctx.shadowBlur = 6;
  ctx.beginPath();
  ctx.ellipse(0, 0, width, height, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.shadowBlur = 0;
  ctx.globalAlpha *= 0.45;
  ctx.fillStyle = 'rgba(255, 248, 224, 0.58)';
  ctx.beginPath();
  ctx.ellipse(-width * 0.22, -height * 0.24, width * 0.34, height * 0.2, -0.35, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
};

const drawDropletFallback = (ctx: CanvasRenderingContext2D, droplet: GooDroplet): void => {
  const speedMagnitude = Math.hypot(droplet.vx, droplet.vy);
  const stretch = clamp(speedMagnitude / 10, 1, 6);
  const width = droplet.baseSize * 0.28 * droplet.sizeFactor;
  const height = droplet.baseSize * 0.65 * stretch * droplet.sizeFactor;
  const gradient = ctx.createLinearGradient(0, -height, 0, height);
  gradient.addColorStop(0, 'rgba(255, 246, 218, 0.84)');
  gradient.addColorStop(0.4, 'rgba(253, 239, 207, 0.74)');
  gradient.addColorStop(1, 'rgba(244, 228, 202, 0.58)');
  ctx.save();
  ctx.translate(droplet.x, droplet.y);
  ctx.rotate(droplet.rotation);
  ctx.globalAlpha = droplet.alpha;
  ctx.fillStyle = gradient;
  ctx.shadowColor = GOO_SHADOW;
  ctx.shadowBlur = 6;
  ctx.beginPath();
  ctx.ellipse(0, 0, width, height, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.shadowBlur = 0;
  ctx.globalAlpha *= 0.4;
  ctx.fillStyle = 'rgba(255, 248, 224, 0.55)';
  ctx.beginPath();
  ctx.ellipse(-width * 0.2, -height * 0.26, width * 0.32, height * 0.18, -0.35, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
};

const drawDebugOverlay = (ctx: CanvasRenderingContext2D, debug: GooDebugMetrics): void => {
  const lines = [
    `frame: ${debug.frame}`,
    `activeStrand: ${debug.activeStrandId ?? 'none'}`,
    `activePointerId: ${debug.activePointerId ?? 'none'} pointer=${debug.pointerActive ? 'on' : 'off'}`,
    `strands: ${debug.strands} drawing: ${debug.drawingStrands}`,
    `stuckLinks: ${debug.stuckLinks} lockWrites: ${debug.lockWrites}`,
    `lastFinalize: ${debug.lastFinalizeReason}`,
  ];
  const x = 12;
  const y = 12;
  const lineHeight = 14;
  const boxWidth = 360;
  const boxHeight = lines.length * lineHeight + 12;

  ctx.save();
  ctx.globalAlpha = 0.9;
  ctx.fillStyle = 'rgba(0, 0, 0, 0.62)';
  ctx.fillRect(x, y, boxWidth, boxHeight);
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.28)';
  ctx.strokeRect(x, y, boxWidth, boxHeight);
  ctx.font = '12px monospace';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  ctx.fillStyle = '#c8ffda';
  for (let i = 0; i < lines.length; i += 1) {
    ctx.fillText(lines[i] as string, x + 8, y + 6 + i * lineHeight);
  }
  ctx.restore();
};

const drawCursorSprite = (
  ctx: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
  image: HTMLImageElement,
  state: CursorSpriteState,
  now: number
): void => {
  if (!state.active || !state.position) return;

  const rect = canvas.getBoundingClientRect();
  const scaleX = rect.width > 0 ? canvas.width / rect.width : 1;
  const scaleY = rect.height > 0 ? canvas.height / rect.height : scaleX;
  const drawWidth = CURSOR_SPRITE_DRAW_WIDTH * scaleX;
  const drawHeight = drawWidth * (CURSOR_SPRITE_SOURCE_FRAME_HEIGHT / CURSOR_SPRITE_SOURCE_FRAME_WIDTH);
  const targetX = state.position.x + CURSOR_SPRITE_OFFSET_X * scaleX;
  const targetY = state.position.y + CURSOR_SPRITE_OFFSET_Y * scaleY;
  const elapsedSeconds = Math.max(0, (now - state.startedAt) / 1000);
  const frameIndex = Math.floor(elapsedSeconds * CURSOR_SPRITE_FRAME_RATE) % CURSOR_SPRITE_FRAME_COUNT;

  ctx.drawImage(
    image,
    frameIndex * CURSOR_SPRITE_SOURCE_FRAME_WIDTH,
    0,
    CURSOR_SPRITE_SOURCE_FRAME_WIDTH,
    CURSOR_SPRITE_SOURCE_FRAME_HEIGHT,
    targetX,
    targetY,
    drawWidth,
    drawHeight
  );
};

const spawnOverflowDroplet = (
  dropletsRef: React.MutableRefObject<GooDroplet[]>,
  nextIdRef: React.MutableRefObject<number>,
  point: Point,
  velocity: Point,
  thickness: number,
  alpha = 0.95
): void => {
  dropletsRef.current.push({
    id: nextIdRef.current,
    x: point.x,
    y: point.y,
    vx: velocity.x,
    vy: velocity.y,
    alpha,
    age: 0,
    baseSize: 12 * thickness,
    sizeFactor: 1,
    sizeDecrease: 0.02,
    rotation: Math.atan2(velocity.y, velocity.x) + Math.PI / 2,
  });
  nextIdRef.current += 1;
};

const getAirborneAnchorDelay = (velocity: Point, baseDelay: number): number => {
  const upwardBias = clamp(-velocity.y / 18, 0, 1);
  const bonus = Math.round(upwardBias * UPWARD_AIRTIME_BONUS);
  return Math.max(baseDelay, MIN_LINK_AIRTIME_DELAY + bonus);
};

const getNormalizedOrDown = (velocity: Point): Point => {
  const mag = Math.hypot(velocity.x, velocity.y);
  if (mag < 0.001) return { x: 0, y: 1 };
  return { x: velocity.x / mag, y: velocity.y / mag };
};

const applyUpwardLaunch = (velocity: Point, thickness: number): Point => {
  const launchBoost = BASE_UPWARD_LAUNCH * (0.9 + thickness * 0.22);
  return {
    x: velocity.x,
    y: Math.max(-UPWARD_SPEED_CAP, velocity.y - launchBoost),
  };
};

const markReleasedTail = (strand: GooStrand, releaseVelocity: Point): void => {
  strand.drawing = false;
  strand.age = 0;
  const tail = strand.links[strand.links.length - 1];
  if (!tail) return;
  tail.freeTail = true;
  tail.stuck = false;
  tail.vx = releaseVelocity.x;
  tail.vy = releaseVelocity.y;
  tail.anchorAge = 0;
  tail.anchorDelay = getAirborneAnchorDelay(releaseVelocity, strand.collisionDelay);
};

const getNextAnchorableLinkIndex = (strand: GooStrand): number =>
  strand.links.findIndex((link) => !link.freeTail && !link.stuck);

const canAnchorLink = (link: GooLink, index: number, strand: GooStrand): boolean =>
  !link.freeTail && !link.stuck && index === getNextAnchorableLinkIndex(strand);

export const GooMiniGame = ({
  imageSrc,
  label,
  onBackToResults,
  backButtonClassName,
  onBackPointerDownCapture,
  onBackTouchStartCapture,
  onBackMouseDownCapture,
}: GooMiniGameProps) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const dropletImageRef = useRef<HTMLImageElement | null>(null);
  const cursorSpriteImageRef = useRef<HTMLImageElement | null>(null);
  const strandsRef = useRef<GooStrand[]>([]);
  const dropletsRef = useRef<GooDroplet[]>([]);
  const persistedStrandsRef = useRef<PersistedStrandMark[]>([]);
  const persistedDropletsRef = useRef<GooDroplet[]>([]);
  const cursorSpriteRef = useRef<CursorSpriteState>({
    active: false,
    followsPointer: false,
    position: null,
    startedAt: 0,
  });
  const pointerRef = useRef<Point | null>(null);
  const pointerButtonsRef = useRef(0);
  const smoothedPointerRef = useRef<Point | null>(null);
  const emissionVelocityRef = useRef<Point>({ x: 0, y: 0 });
  const activeStrandIdRef = useRef<number | null>(null);
  const activePointerIdRef = useRef<number | null>(null);
  const debugRef = useRef<GooDebugMetrics>({
    frame: 0,
    activeStrandId: null,
    activePointerId: null,
    pointerActive: false,
    strands: 0,
    drawingStrands: 0,
    stuckLinks: 0,
    lockWrites: 0,
    lastFinalizeReason: 'init',
  });
  const nextIdRef = useRef(1);
  const animationRef = useRef<number | null>(null);
  const imageMetricsRef = useRef<ImageMetrics | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [wipeMode, setWipeMode] = useState(false);
  const [thickness, setThickness] = useState(2.5);
  const [sharePreviewImage, setSharePreviewImage] = useState<string | null>(null);
  const [shareError, setShareError] = useState<string | null>(null);
  const [shareGenerating, setShareGenerating] = useState(false);
  const thicknessRef = useRef(thickness);

  const clearGoo = useCallback(() => {
    strandsRef.current = [];
    dropletsRef.current = [];
    persistedStrandsRef.current = [];
    persistedDropletsRef.current = [];
  }, []);

  const resizeCanvas = useCallback((width: number, height: number): void => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (canvas.width === width && canvas.height === height) return;
    canvas.width = width;
    canvas.height = height;
  }, []);

  const ensureCanvasReady = useCallback((): void => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const ratio = window.devicePixelRatio || 1;
    const width = Math.max(320, Math.round(rect.width * ratio));
    const height = Math.max(320, Math.round(rect.height * ratio));
    resizeCanvas(width, height);
    const image = imageRef.current;
    if (image && (!imageMetricsRef.current || imageMetricsRef.current.width === 0)) {
      imageMetricsRef.current = getContainedImageMetrics(image, canvas.width, canvas.height);
    }
  }, [resizeCanvas]);

  const commitStrandToPersistence = useCallback((links: GooLink[], alpha: number, markThickness?: number): void => {
    if (links.length < 2) return;
    const clonedLinks = links.map((link) => ({ ...link }));
    persistedStrandsRef.current.push({
      links: clonedLinks,
      alpha,
      thickness: markThickness ?? thicknessRef.current,
    });
  }, []);

  const commitDropletToPersistence = useCallback((droplet: GooDroplet): void => {
    persistedDropletsRef.current.push({ ...droplet });
  }, []);

  const finishActiveStrand = useCallback((reason: string) => {
    debugRef.current.lastFinalizeReason = reason;
    const activeId = activeStrandIdRef.current;
    if (activeId != null) {
      const strand = strandsRef.current.find((candidate) => candidate.id === activeId);
      if (strand) {
        markReleasedTail(strand, applyUpwardLaunch(emissionVelocityRef.current, thicknessRef.current));
      }
    }

    activeStrandIdRef.current = null;
    pointerRef.current = null;
    smoothedPointerRef.current = null;
    emissionVelocityRef.current = { x: 0, y: 0 };
    activePointerIdRef.current = null;
    pointerButtonsRef.current = 0;
  }, []);

  const captureShareImage = useCallback(async (): Promise<string> => {
    ensureCanvasReady();
    finishActiveStrand('share-capture');
    cursorSpriteRef.current = {
      active: false,
      followsPointer: false,
      position: null,
      startedAt: 0,
    };

    try {
      const corsImage = await loadCorsImageFromUrl(imageSrc);
      imageRef.current = corsImage;
      setImageLoaded(true);
      const canvas = canvasRef.current;
      if (canvas) {
        imageMetricsRef.current = getContainedImageMetrics(corsImage, canvas.width, canvas.height);
      }
    } catch {
      throw new Error('Could not export this image because the image host does not allow browser downloads.');
    }

    await waitForAnimationFrame();
    await waitForAnimationFrame();

    const sourceCanvas = canvasRef.current;
    const metrics = imageMetricsRef.current;
    if (!sourceCanvas || !metrics || !imageLoaded) {
      throw new Error('The image is still loading. Try sharing again in a moment.');
    }

    const cropX = clamp(metrics.x, 0, sourceCanvas.width);
    const cropY = clamp(metrics.y, 0, sourceCanvas.height);
    const cropWidth = clamp(metrics.width, 1, sourceCanvas.width - cropX);
    const cropHeight = clamp(metrics.height, 1, sourceCanvas.height - cropY);
    const scale = Math.min(1, SHARE_MAX_DIMENSION / cropWidth, SHARE_MAX_DIMENSION / cropHeight);
    const outputWidth = Math.max(1, Math.round(cropWidth * scale));
    const outputHeight = Math.max(1, Math.round(cropHeight * scale));
    const outputCanvas = document.createElement('canvas');
    outputCanvas.width = outputWidth;
    outputCanvas.height = outputHeight;

    const ctx = outputCanvas.getContext('2d');
    if (!ctx) {
      throw new Error('Image generation is not supported in this browser.');
    }

    ctx.fillStyle = '#150020';
    ctx.fillRect(0, 0, outputWidth, outputHeight);
    ctx.drawImage(sourceCanvas, cropX, cropY, cropWidth, cropHeight, 0, 0, outputWidth, outputHeight);
    drawShareWatermark(ctx, outputWidth, outputHeight);

    try {
      return outputCanvas.toDataURL('image/png');
    } catch {
      throw new Error('Could not export this image because the image host does not allow browser downloads.');
    }
  }, [ensureCanvasReady, finishActiveStrand, imageLoaded, imageSrc]);

  const handleGenerateShareImage = useCallback(async () => {
    setShareGenerating(true);
    setShareError(null);
    try {
      const dataUrl = await captureShareImage();
      setSharePreviewImage(dataUrl);
    } catch (error) {
      setShareError(error instanceof Error ? error.message : 'Failed to generate share image.');
    } finally {
      setShareGenerating(false);
    }
  }, [captureShareImage]);

  const closeSharePreview = useCallback(() => {
    setSharePreviewImage(null);
  }, []);

  useEffect(() => {
    const dropletImage = new Image();
    dropletImage.src = gooDropletAsset;
    dropletImage.onload = () => {
      dropletImageRef.current = dropletImage;
    };
  }, []);

  useEffect(() => {
    const cursorSpriteImage = new Image();
    cursorSpriteImage.src = cursorSpriteAsset;
    cursorSpriteImage.onload = () => {
      cursorSpriteImageRef.current = cursorSpriteImage;
    };
    cursorSpriteImage.onerror = () => {
      cursorSpriteImageRef.current = null;
    };
  }, []);

  useEffect(() => {
    setImageLoaded(false);
    clearGoo();

    let cancelled = false;
    const loadImage = (useCors: boolean): void => {
      const image = new Image();
      if (useCors) {
        image.crossOrigin = 'anonymous';
      }
      image.onload = () => {
        if (cancelled) return;
        imageRef.current = image;
        setImageLoaded(true);
      };
      image.onerror = () => {
        if (cancelled) return;
        if (useCors) {
          loadImage(false);
          return;
        }
        imageRef.current = null;
        setImageLoaded(false);
      };
      image.src = useCors ? addCacheBustParam(imageSrc) : imageSrc;
    };

    loadImage(true);
    return () => {
      cancelled = true;
    };
  }, [clearGoo, imageSrc]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const ratio = window.devicePixelRatio || 1;
      const width = Math.max(320, Math.round(rect.width * ratio));
      const height = Math.max(320, Math.round(rect.height * ratio));
      resizeCanvas(width, height);
    };

    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(canvas);
    return () => {
      observer.disconnect();
    };
  }, [resizeCanvas]);

  useEffect(() => {
    thicknessRef.current = thickness;
  }, [thickness]);

  useEffect(() => {
    const tick = () => {
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      if (!canvas || !ctx) {
        animationRef.current = window.requestAnimationFrame(tick);
        return;
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = '#150020';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const image = imageRef.current;
      if (image) {
        imageMetricsRef.current = drawContainedImage(ctx, image, canvas.width, canvas.height);
      } else {
        ctx.fillStyle = '#31004f';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = '#ffffff';
        ctx.font = `${Math.max(18, canvas.width * 0.035)}px sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('Image unavailable', canvas.width / 2, canvas.height / 2);
      }

      for (const mark of persistedStrandsRef.current) {
        drawGooPath(ctx, mark.links, mark.thickness, mark.alpha);
      }
      for (const droplet of persistedDropletsRef.current) {
        drawLockedDroplet(ctx, droplet);
      }

      const activePointer = pointerRef.current;
      const activeId = activeStrandIdRef.current;
      const metrics = imageMetricsRef.current;
      const speedMult = getSpeedMultiplier(thickness);
      let lockWritesThisFrame = 0;

      if (activeStrandIdRef.current != null && pointerRef.current && pointerButtonsRef.current === 0) {
        finishActiveStrand('buttons-zero-fallback');
      }

      if (activePointer) {
        const smoothed = smoothedPointerRef.current ?? { ...activePointer };
        const delta = { x: activePointer.x - smoothed.x, y: activePointer.y - smoothed.y };
        if (delta.x * delta.x + delta.y * delta.y > SPEED_SAMPLE_THRESHOLD_SQ) {
          smoothed.x += delta.x / 4;
          smoothed.y += delta.y / 4;
        }
        smoothedPointerRef.current = smoothed;
        const velocity = { x: activePointer.x - smoothed.x, y: activePointer.y - smoothed.y };
        const speed = Math.hypot(velocity.x, velocity.y);
        if (speed > SPEED_CLAMP_INPUT) {
          const scale = SPEED_CLAMP_OUTPUT / speed;
          velocity.x *= scale;
          velocity.y *= scale;
        }
        velocity.x *= speedMult;
        velocity.y *= speedMult;
        emissionVelocityRef.current = velocity;
      }

      strandsRef.current = strandsRef.current.filter((strand) => {
        strand.age += 1;

        if (strand.drawing && activePointer && strand.id === activeId) {
          strand.spurtAge += 1;
          strand.emitTick += 1;
          if (strand.emitTick % LINK_FREQ === 0) {
            const velocityDir = getNormalizedOrDown(emissionVelocityRef.current);
            const spawnPoint = {
              x: activePointer.x - velocityDir.x * LINK_SPACING * 0.35,
              y: activePointer.y - velocityDir.y * LINK_SPACING * 0.35,
            };
            const launchedVelocity = applyUpwardLaunch(emissionVelocityRef.current, thickness);
            strand.links.push(
              createLink(
                spawnPoint,
                launchedVelocity,
                Math.min(randomCumMass(thickness), TAIL_MAX_MASS),
                false,
                getAirborneAnchorDelay(launchedVelocity, strand.collisionDelay)
              )
            );
            if (strand.links.length > MAX_STRAND_LINKS) {
              const removed = strand.links.shift();
              const successor = strand.links[0];
              if (removed?.freeTail && successor) {
                successor.freeTail = true;
              }
              if (removed?.stuck && successor) {
                commitStrandToPersistence([removed, successor], 0.95, thickness);
                lockWritesThisFrame += 1;
              }
            }
          }
          if (strand.emitTick % (LINK_FREQ * 10) === 0 && strand.collisionDelay > 0) {
            strand.collisionDelay -= 1;
          }
          if (strand.spurtAge >= MAX_SPURT_FRAMES) {
            markReleasedTail(strand, applyUpwardLaunch(emissionVelocityRef.current, thickness));
          }
        }

        for (let i = 0; i < strand.links.length; i += 1) {
          const link = strand.links[i];
          if (!link) continue;
          if (link.stuck) {
            link.anchorAge += 1;
            if (metrics) {
              const wetness = clamp(1 - link.anchorAge / WET_LOCK_AFTER_FRAMES, 0, 1);
              link.x += Math.sin((strand.age + i * 11) / 18) * 0.08 * wetness;
              link.x = clamp(link.x, metrics.x, metrics.x + metrics.width);
              link.y = clamp(link.y, metrics.y, metrics.y + metrics.height);
            }
            continue;
          }

          const prev = strand.links[i - 1];
          const next = strand.links[i + 1];
          const prevDelta = prev ? { x: prev.x - link.x, y: prev.y - link.y } : { x: 0, y: 0 };
          const nextDelta = next ? { x: next.x - link.x, y: next.y - link.y } : { x: 0, y: 0 };
          const tailDamping = link.freeTail ? FREE_TAIL_SPRING_DAMPING : 1;
          const gravityScale = link.freeTail ? FREE_TAIL_GRAVITY_SCALE : 1;
          link.vx += ((prevDelta.x + nextDelta.x) / MOVE_SMOOTHING) * tailDamping;
          link.vy += ((prevDelta.y + nextDelta.y) / MOVE_SMOOTHING) * tailDamping + GRAVITY * link.mass * gravityScale;
          link.x += link.vx;
          link.y += link.vy;
          const friction = link.freeTail ? FRICTION * FREE_TAIL_VELOCITY_DAMPING : FRICTION;
          link.vx /= friction;
          link.vy /= friction;
          if (link.anchorDelay > 0) {
            link.anchorDelay -= 1;
          }
        }

        for (let i = 0; i < strand.links.length; i += 1) {
          const link = strand.links[i];
          if (!link) continue;
          if (isNewestTailLink(i, strand.links.length)) {
            link.mass = Math.min(link.mass, TAIL_MAX_MASS);
          }
          const neighbours = [strand.links[i - 1], strand.links[i + 1]];
          for (const neighbour of neighbours) {
            if (!neighbour) continue;
            const neighbourIndex = strand.links.indexOf(neighbour);
            if (neighbourIndex >= 0 && isNewestTailLink(neighbourIndex, strand.links.length)) continue;
            if (neighbour.y > link.y && link.mass > MIN_LINK_MASS) {
              const movedMass = Math.min(MASS_FLOW, link.mass - MIN_LINK_MASS);
              if (movedMass > 0) {
                link.mass -= movedMass;
                neighbour.mass += movedMass;
              }
            }
          }
        }

        for (let pass = 0; pass < 3; pass += 1) {
          for (let i = 1; i < strand.links.length; i += 1) {
            const prev = strand.links[i - 1];
            const current = strand.links[i];
            if (!prev || !current) continue;
            const dx = current.x - prev.x;
            const dy = current.y - prev.y;
            const dist = Math.max(0.001, Math.hypot(dx, dy));
            const target = LINK_SPACING * (0.75 + current.mass * 0.35);
            const diff = (dist - target) / dist;
            const offsetX = dx * diff * 0.35;
            const offsetY = dy * diff * 0.35;
            const prevConstraintScale = prev.freeTail ? FREE_TAIL_CONSTRAINT_SCALE : 1;
            const currentConstraintScale = current.freeTail ? FREE_TAIL_CONSTRAINT_SCALE : 1;
            if (!prev.stuck) {
              prev.x += offsetX * prevConstraintScale;
              prev.y += offsetY * prevConstraintScale;
            }
            if (!current.stuck) {
              current.x -= offsetX * currentConstraintScale;
              current.y -= offsetY * currentConstraintScale;
            }
          }
        }

        for (let i = 0; i < strand.links.length; i += 1) {
          const link = strand.links[i];
          if (!link || !metrics) continue;
          const startLink = strand.links[0];
          const tailCloseToStart =
            Boolean(startLink) &&
            i >= strand.links.length - DRAWING_FREE_TAIL_LINKS &&
            distance(link, startLink as GooLink) < LINK_SPACING * END_START_MIN_DIST_FACTOR &&
            strand.age < END_START_PROTECT_FRAMES;
          if (link.stuck) {
            link.x = clamp(link.x, metrics.x, metrics.x + metrics.width);
            link.y = clamp(link.y, metrics.y, metrics.y + metrics.height);
            continue;
          }
          // Keep a single continuous stream while mouse is held:
          // do not allow active strands to anchor mid-spurt.
          if (strand.drawing) {
            continue;
          }
          const horizontalSmearLanding =
            Math.abs(link.vx) >= HORIZONTAL_STICK_MIN_SPEED &&
            Math.abs(link.vy) <= HORIZONTAL_STICK_VERTICAL_SPEED &&
            Math.abs(link.vx) >= Math.abs(link.vy) * HORIZONTAL_STICK_DOMINANCE;
          if (canAnchorLink(link, i, strand) && link.y > metrics.y + metrics.height + 6) {
            stickLinkToImage(link, metrics);
          } else if (
            canAnchorLink(link, i, strand) &&
            link.anchorDelay <= 0 &&
            isPointInImage(link, metrics) &&
            i < strand.links.length - 1 &&
            (Math.hypot(link.vx, link.vy) <= LANDING_SPEED_THRESHOLD || horizontalSmearLanding)
          ) {
            if (!tailCloseToStart) {
              stickLinkToImage(link, metrics);
            }
          } else if (
            canAnchorLink(link, i, strand) &&
            !strand.drawing &&
            strand.age > 120 + i * 4 &&
            Math.hypot(link.vx, link.vy) < 0.35
          ) {
            if (!tailCloseToStart) {
              stickLinkToImage(link, metrics);
            }
          }
        }

        if (!strand.drawing && strand.age > FORCE_LOCK_AFTER_FRAMES && metrics) {
          const nextAnchorIndex = getNextAnchorableLinkIndex(strand);
          const nextAnchorLink = nextAnchorIndex >= 0 ? strand.links[nextAnchorIndex] : undefined;
          if (nextAnchorLink) {
            stickLinkToImage(nextAnchorLink, metrics);
          }
        }

        for (let i = 0; i < strand.links.length; i += 1) {
          const link = strand.links[i];
          if (isNewestTailLink(i, strand.links.length) && link) {
            link.mass = Math.min(link.mass, TAIL_MAX_MASS);
          }
          if (isNewestTailLink(i, strand.links.length) || link?.freeTail) continue;
          if (!link || link.mass < MAX_LINK_MASS) continue;
          link.mass /= 2;
          const splitLink = createLink(
            { x: link.x + 1, y: link.y },
            { x: link.vx, y: link.vy },
            link.mass,
            false,
            link.anchorDelay
          );
          strand.links.splice(i, 0, splitLink);
          spawnOverflowDroplet(
            dropletsRef,
            nextIdRef,
            { x: link.x, y: link.y },
            { x: link.vx, y: link.vy },
            thickness
          );
          if (strand.links.length > MAX_STRAND_LINKS + 1) {
            const removed = strand.links.shift();
            const successor = strand.links[0];
            if (removed?.freeTail && successor) {
              successor.freeTail = true;
            }
          }
          i += 1;
        }

        drawGooPath(ctx, strand.links, thickness);
        const anchorableLinks = strand.links.filter((link) => !link.freeTail);
        const allLinksStuck = anchorableLinks.length > 0 && anchorableLinks.every((link) => link.stuck);
        const allLinksSettled = anchorableLinks.every((link) => !link.stuck || link.anchorAge >= WET_LOCK_AFTER_FRAMES);
        const shouldLock = !strand.drawing && ((allLinksStuck && allLinksSettled) || strand.age > LOCK_AFTER_FRAMES);
        if (shouldLock) {
          commitStrandToPersistence(strand.links, 0.96, thickness);
          lockWritesThisFrame += 1;
          return false;
        }

        return strand.drawing || strand.links.length > 1;
      });

      const dropletImage = dropletImageRef.current;
      dropletsRef.current = dropletsRef.current.filter((droplet) => {
        droplet.age += 1;
        droplet.vx /= FRICTION;
        droplet.vy /= FRICTION;
        droplet.x += droplet.vx;
        droplet.y += droplet.vy + GRAVITY;
        droplet.rotation = Math.atan2(droplet.vy, droplet.vx) + Math.PI / 2;
        droplet.sizeFactor -= droplet.sizeDecrease;

        const metricsLocal = imageMetricsRef.current;
        if (metricsLocal && isPointInImage(droplet, metricsLocal) && droplet.age > 1) {
          const persistedSize = droplet.baseSize * droplet.sizeFactor;
          if (persistedSize >= MIN_PERSIST_DROPLET_SIZE) {
            commitDropletToPersistence(droplet);
            lockWritesThisFrame += 1;
          }
          return false;
        }
        if (
          metricsLocal &&
          droplet.y >= metricsLocal.y + metricsLocal.height &&
          droplet.x >= metricsLocal.x &&
          droplet.x <= metricsLocal.x + metricsLocal.width
        ) {
          droplet.y = metricsLocal.y + metricsLocal.height;
          const persistedSize = droplet.baseSize * droplet.sizeFactor;
          if (persistedSize >= MIN_PERSIST_DROPLET_SIZE) {
            commitDropletToPersistence(droplet);
            lockWritesThisFrame += 1;
          }
          return false;
        }

        const speedMagnitude = Math.hypot(droplet.vx, droplet.vy);
        const stretch = clamp(speedMagnitude / 10, 1, 6);
        const width = droplet.baseSize * 0.7 * droplet.sizeFactor;
        const height = droplet.baseSize * 0.9 * stretch * droplet.sizeFactor;
        const alpha = clamp(droplet.alpha * droplet.sizeFactor, 0, 1);

        if (dropletImage) {
          ctx.save();
          ctx.translate(droplet.x, droplet.y);
          ctx.rotate(droplet.rotation);
          ctx.globalAlpha = alpha;
          ctx.drawImage(dropletImage, -width / 2, -height / 2, width, height);
          ctx.restore();
        } else {
          droplet.alpha = alpha;
          drawDropletFallback(ctx, droplet);
        }

        return (
          droplet.sizeFactor > 0 &&
          droplet.x > -40 &&
          droplet.x < canvas.width + 40 &&
          droplet.y < canvas.height + 60
        );
      });

      const cursorSpriteImage = cursorSpriteImageRef.current;
      if (cursorSpriteImage) {
        drawCursorSprite(ctx, canvas, cursorSpriteImage, cursorSpriteRef.current, performance.now());
      }

      if (DEBUG_GOO_OVERLAY) {
        let drawingStrands = 0;
        let stuckLinks = 0;
        for (const strand of strandsRef.current) {
          if (strand.drawing) {
            drawingStrands += 1;
          }
          for (const link of strand.links) {
            if (link.stuck) {
              stuckLinks += 1;
            }
          }
        }
        debugRef.current.frame += 1;
        debugRef.current.activeStrandId = activeStrandIdRef.current;
        debugRef.current.activePointerId = activePointerIdRef.current;
        debugRef.current.pointerActive = pointerRef.current != null;
        debugRef.current.strands = strandsRef.current.length;
        debugRef.current.drawingStrands = drawingStrands;
        debugRef.current.stuckLinks = stuckLinks;
        debugRef.current.lockWrites += lockWritesThisFrame;
        drawDebugOverlay(ctx, debugRef.current);
      }

      animationRef.current = window.requestAnimationFrame(tick);
    };

    animationRef.current = window.requestAnimationFrame(tick);
    return () => {
      if (animationRef.current != null) {
        window.cancelAnimationFrame(animationRef.current);
      }
    };
  }, [finishActiveStrand, thickness]);

  const eraseAt = (point: Point) => {
    const eraseRadius = 34 * thickness;
    persistedStrandsRef.current = persistedStrandsRef.current.filter((mark) =>
      mark.links.every((link) => distance(link, point) > eraseRadius)
    );
    persistedDropletsRef.current = persistedDropletsRef.current.filter((droplet) => distance(droplet, point) > eraseRadius);
    strandsRef.current = strandsRef.current.filter((strand) => strand.links.every((link) => distance(link, point) > 34 * thickness));
    dropletsRef.current = dropletsRef.current.filter((droplet) => distance(droplet, point) > 34 * thickness);
  };

  const activateCursorSprite = (point: Point, pointerType: string): void => {
    cursorSpriteRef.current = {
      active: true,
      followsPointer: pointerType !== 'touch',
      position: { ...point },
      startedAt: performance.now(),
    };
  };

  const updateCursorSpritePosition = (point: Point): void => {
    const cursorSprite = cursorSpriteRef.current;
    if (!cursorSprite.active || !cursorSprite.followsPointer) return;
    cursorSprite.position = { ...point };
  };

  const stopCursorSpriteFollow = (): void => {
    const cursorSprite = cursorSpriteRef.current;
    if (cursorSprite.followsPointer) {
      cursorSprite.active = false;
    }
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLCanvasElement>) => {
    ensureCanvasReady();
    const canvas = canvasRef.current;
    if (!canvas) return;
    event.preventDefault();
    const point = getPointerPoint(event, canvas);
    activateCursorSprite(point, event.pointerType);
    finishActiveStrand('pointerdown-new-strand');
    event.currentTarget.setPointerCapture(event.pointerId);
    activePointerIdRef.current = event.pointerId;
    pointerButtonsRef.current = 1;
    pointerRef.current = point;
    smoothedPointerRef.current = { ...point };
    emissionVelocityRef.current = { x: 0, y: 0 };

    if (wipeMode) {
      eraseAt(point);
      return;
    }

    const sourceLink = createLink(point, applyUpwardLaunch({ x: 0, y: 0 }, thickness), randomCumMass(thickness), false);
    const strand: GooStrand = {
      id: nextIdRef.current,
      links: [sourceLink],
      age: 0,
      drawing: true,
      source: point,
      collisionDelay: Math.floor(Math.random() * 2 + 1),
      emitTick: 0,
      spurtAge: 0,
    };
    activeStrandIdRef.current = strand.id;
    nextIdRef.current += 1;
    strandsRef.current.push(strand);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
    ensureCanvasReady();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const point = getPointerPoint(event, canvas);
    if (isPointInCanvasBounds(point, canvas)) {
      updateCursorSpritePosition(point);
    } else {
      stopCursorSpriteFollow();
    }
    if (pointerRef.current == null) return;
    event.preventDefault();
    pointerButtonsRef.current = event.buttons;
    pointerRef.current = point;

    if (wipeMode) {
      eraseAt(point);
    }
  };

  const finishPointer = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    pointerButtonsRef.current = 0;
    finishActiveStrand('canvas-pointerup');
  };

  const handleLostPointerCapture = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const activePointerId = activePointerIdRef.current;
    if (activePointerId == null || event.pointerId === activePointerId) {
      pointerButtonsRef.current = 0;
      finishActiveStrand('lost-pointer-capture');
    }
  };

  useEffect(() => {
    const finishFromWindow = (reason: string) => {
      const canvas = canvasRef.current;
      const pointerId = activePointerIdRef.current;
      if (canvas && pointerId != null && canvas.hasPointerCapture(pointerId)) {
        canvas.releasePointerCapture(pointerId);
      }
      finishActiveStrand(reason);
    };

    const onVisibilityChange = () => {
      if (document.hidden) {
        finishFromWindow('document-hidden');
      }
    };

    const onWindowPointerUp = () => {
      pointerButtonsRef.current = 0;
      finishFromWindow('window-pointerup');
    };
    const onWindowPointerCancel = () => finishFromWindow('window-pointercancel');
    const onWindowBlur = () => {
      pointerButtonsRef.current = 0;
      finishFromWindow('window-blur');
    };
    const onWindowMouseUp = () => {
      pointerButtonsRef.current = 0;
      finishFromWindow('window-mouseup');
    };

    window.addEventListener('pointerup', onWindowPointerUp);
    window.addEventListener('pointercancel', onWindowPointerCancel);
    window.addEventListener('blur', onWindowBlur);
    window.addEventListener('mouseup', onWindowMouseUp);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('pointerup', onWindowPointerUp);
      window.removeEventListener('pointercancel', onWindowPointerCancel);
      window.removeEventListener('blur', onWindowBlur);
      window.removeEventListener('mouseup', onWindowMouseUp);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [finishActiveStrand]);

  return (
    <div className="aura-panel select-none rounded-2xl bg-[#5c0099] p-3 text-white sm:p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-[#fdc500]">Cum shot mini game</p>
          <h2 className="text-xl font-black sm:text-2xl">Give {label} a cum shot</h2>
          <p className="text-xs text-white/75"> Click and Drag to cum; switch to wipe to clean.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            className={clsx('rounded-full px-3 py-2 text-xs font-semibold text-white btn-3d-purple', backButtonClassName)}
            onPointerDownCapture={onBackPointerDownCapture}
            onTouchStartCapture={onBackTouchStartCapture}
            onMouseDownCapture={onBackMouseDownCapture}
            onClick={onBackToResults}
          >
            Done (+5 exp)
          </button>
          <button
            type="button"
            className="rounded-full px-3 py-2 text-xs font-semibold text-white btn-3d-gold"
            onClick={clearGoo}
          >
            Clear Cum 
          </button>
          <button
            type="button"
            className="rounded-full px-3 py-2 text-xs font-semibold text-white btn-3d-gold disabled:opacity-60"
            onClick={() => void handleGenerateShareImage()}
            disabled={shareGenerating || !imageLoaded}
          >
            {shareGenerating ? 'Sharing...' : 'Share'}
          </button>
        </div>
      </div>
      {shareError ? <p className="mb-3 text-xs font-semibold text-red-200">{shareError}</p> : null}

      <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_220px]">
        <div className="relative min-h-[420px] overflow-hidden rounded-2xl border border-white/20 bg-black shadow-inner">
          {!imageLoaded ? (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm font-semibold text-white/75">
              Loading {label}...
            </div>
          ) : null}
          <canvas
            ref={canvasRef}
            tabIndex={0}
            className="h-[64vh] min-h-[420px] w-full touch-none cursor-crosshair"
            style={{ touchAction: 'none' }}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerCancel={finishPointer}
            onPointerLeave={(event) => {
              stopCursorSpriteFollow();
              if (pointerRef.current) finishPointer(event);
            }}
            onPointerUp={finishPointer}
            onLostPointerCapture={handleLostPointerCapture}
          />
        </div>

        <div className="aura-card rounded-2xl bg-[#3d0066] p-3">
          <div className="space-y-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-[#fdc500]">Tools</p>
              <div className="mt-2 grid grid-cols-2 gap-2 lg:grid-cols-1">
                <button
                  type="button"
                  className={`rounded-full px-3 py-2 text-xs font-semibold text-white ${
                    !wipeMode ? 'btn-3d-gold' : 'btn-3d-purple'
                  }`}
                  onClick={() => setWipeMode(false)}
                >
                  Cum
                </button>
                <button
                  type="button"
                  className={`rounded-full px-3 py-2 text-xs font-semibold text-white ${
                    wipeMode ? 'btn-3d-gold' : 'btn-3d-purple'
                  }`}
                  onClick={() => setWipeMode(true)}
                >
                  Wipe
                </button>
              </div>
            </div>

            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wide text-[#fdc500]">Thickness</span>
              <input
                type="range"
                min="0.7"
                max="1.8"
                step="0.1"
                value={thickness}
                onChange={(event) => setThickness(Number(event.target.value))}
                className="mt-2 w-full accent-[#fdc500]"
              />
            </label>

          </div>
        </div>
      </div>
      {sharePreviewImage ? (
        <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/70 px-4">
          <div className="aura-panel w-full max-w-3xl rounded-2xl bg-[#5c0099] p-4 shadow-lg">
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-lg font-bold text-white">Share Preview</h3>
              <button
                type="button"
                className="rounded-full bg-[#510087] px-3 py-1 text-sm font-semibold text-white btn-3d-purple"
                onClick={closeSharePreview}
              >
                Close
              </button>
            </div>
            <img
              src={sharePreviewImage}
              alt="FMK goo share preview"
              className="aura-frame mt-3 max-h-[70vh] w-full rounded-xl bg-[#2a0045] object-contain"
            />
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <a
                href={sharePreviewImage}
                download="fmk-goo-share.png"
                className="rounded-full bg-[#fdc500] px-4 py-2 text-sm font-semibold text-white btn-3d-gold"
              >
                Download Image
              </a>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};
